const MAPBOX_TOKEN = "pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw";

// Default depot: 2-6 Commerce Circuit, Yatala QLD 4207
const DEFAULT_DEPOT = { lat: -27.7305, lng: 153.2278 };

const MATRIX_BATCH_SIZE = 25;
let matrixCache = {};

export function getJobCoords(job) {
  if (!job) return null;
  if (job.address && job.address.latitude && job.address.longitude) {
    return { lat: job.address.latitude, lng: job.address.longitude };
  }
  if (job.geopoint) {
    const gp = job.geopoint;
    if (gp.latitude && gp.longitude) return { lat: gp.latitude, lng: gp.longitude };
    if (gp._lat && gp._long) return { lat: gp._lat, lng: gp._long };
  }
  if (job.latitude && job.longitude) return { lat: job.latitude, lng: job.longitude };
  if (job.lat && job.lng) return { lat: job.lat, lng: job.lng };
  return null;
}

function buildAddressString(job) {
  if (job.formattedAddress) return job.formattedAddress;
  if (job.address && typeof job.address === 'object') {
    const a = job.address;
    const parts = [a.street_number, a.route, a.locality, a.administrative_area_level_1, a.postal_code, a.country].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
  }
  if (typeof job.address === 'string' && job.address.length > 3) return job.address;
  return null;
}

export async function geocodeMissingJobs(jobs) {
  let geocoded = 0;
  const toGeocode = jobs.filter(j => !getJobCoords(j));

  for (const job of toGeocode) {
    const addressStr = buildAddressString(job);
    if (!addressStr) continue;

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(addressStr)}.json?access_token=${MAPBOX_TOKEN}&country=au&limit=1`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        job.latitude = lat;
        job.longitude = lng;
        job._geocoded = true;
        geocoded++;
      }
    } catch (e) {
      console.error('Geocode error for job:', job.id, e);
    }
  }

  return geocoded;
}

export function formatJobAddress(job) {
  if (!job) return '';
  if (job.formattedAddress) return job.formattedAddress;
  if (job.address && typeof job.address === 'object') {
    const a = job.address;
    const parts = [a.street_number, a.route, a.locality, a.administrative_area_level_1, a.postal_code].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
  }
  if (typeof job.address === 'string') return job.address;
  return '';
}

export function toDate(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  return new Date(val);
}

export function shouldExcludeJob(job) {
  if (job.projectName && String(job.projectName).trim().toUpperCase() === 'START TIME') return true;
  if (!job.meters && job.meters !== 0) return false;
  const m = String(job.meters).trim().toUpperCase();
  return m === 'START';
}

export function getJobMeters(job) {
  if (!job.meters && job.meters !== 0) return 0;
  const val = parseFloat(job.meters);
  return isNaN(val) ? 0 : val;
}

export function estimateJobDuration(job) {
  // Use per-job duration if set
  if (job.duration) {
    return parseFloat(job.duration) * 60; // seconds
  }
  // Every 50m = 1 test (30 min each), with 30 min gaps between tests
  const meters = getJobMeters(job);
  const numTests = Math.ceil(meters / 50);
  if (numTests <= 0) return 0;
  const testMinutes = numTests * 30;
  const gapMinutes = (numTests - 1) * 30;
  return (testMinutes + gapMinutes) * 60; // seconds
}

export function getContractorStart(contractor, vehicles, overrides) {
  // 1. Session override from the Starts panel in the planner
  if (overrides && overrides[contractor.id]) {
    return overrides[contractor.id];
  }
  // 2. Persistent override set via Fleet page (must have address to confirm it was deliberately set)
  if (contractor.startLocation && contractor.startLocation.lat && contractor.startLocation.lng && contractor.startLocation.address) {
    return { lat: contractor.startLocation.lat, lng: contractor.startLocation.lng };
  }
  // 3. Default: Yatala depot
  return DEFAULT_DEPOT;
}

function matrixCacheKey(coords) {
  return coords.map(c => `${c.lat.toFixed(5)},${c.lng.toFixed(5)}`).join('|');
}

export async function buildDurationMatrix(locations) {
  const key = matrixCacheKey(locations);
  if (matrixCache[key]) return matrixCache[key];

  const n = locations.length;

  if (n <= MATRIX_BATCH_SIZE) {
    const matrix = await fetchMatrix(locations);
    matrixCache[key] = matrix;
    return matrix;
  }

  const matrix = Array.from({ length: n }, () => new Array(n).fill(Infinity));
  for (let i = 0; i < n; i++) matrix[i][i] = 0;

  // Chunk size must be ≤ floor(MATRIX_BATCH_SIZE / 2) so any two chunks combined fit the API limit
  const CHUNK = Math.floor(MATRIX_BATCH_SIZE / 2);

  for (let si = 0; si < n; si += CHUNK) {
    for (let di = 0; di < n; di += CHUNK) {
      const sourceIndices = [];
      const destIndices = [];
      const batchCoords = [];
      const coordMap = {};

      for (let s = si; s < Math.min(si + CHUNK, n); s++) {
        const coordStr = `${locations[s].lng},${locations[s].lat}`;
        if (!(coordStr in coordMap)) {
          coordMap[coordStr] = batchCoords.length;
          batchCoords.push(locations[s]);
        }
        sourceIndices.push({ original: s, batch: coordMap[coordStr] });
      }

      for (let d = di; d < Math.min(di + CHUNK, n); d++) {
        const coordStr = `${locations[d].lng},${locations[d].lat}`;
        if (!(coordStr in coordMap)) {
          coordMap[coordStr] = batchCoords.length;
          batchCoords.push(locations[d]);
        }
        destIndices.push({ original: d, batch: coordMap[coordStr] });
      }

      if (batchCoords.length > MATRIX_BATCH_SIZE) continue;

      const srcBatchIds = [...new Set(sourceIndices.map(s => s.batch))];
      const dstBatchIds = [...new Set(destIndices.map(d => d.batch))];

      const batchMatrix = await fetchMatrix(batchCoords, srcBatchIds, dstBatchIds);

      for (let si2 = 0; si2 < srcBatchIds.length; si2++) {
        for (let di2 = 0; di2 < dstBatchIds.length; di2++) {
          const srcOriginals = sourceIndices.filter(s => s.batch === srcBatchIds[si2]);
          const dstOriginals = destIndices.filter(d => d.batch === dstBatchIds[di2]);
          const dur = batchMatrix[si2][di2];
          for (const so of srcOriginals) {
            for (const doo of dstOriginals) {
              if (dur != null && dur !== Infinity) {
                matrix[so.original][doo.original] = dur;
              }
            }
          }
        }
      }
    }
  }

  matrixCache[key] = matrix;
  return matrix;
}

async function fetchMatrix(locations, sourceIndices, destIndices) {
  const coordStr = locations.map(l => `${l.lng},${l.lat}`).join(';');
  let url = `https://api.mapbox.com/directions-matrix/v1/mapbox/driving/${coordStr}?access_token=${MAPBOX_TOKEN}&annotations=duration`;

  if (sourceIndices) url += `&sources=${sourceIndices.join(';')}`;
  if (destIndices) url += `&destinations=${destIndices.join(';')}`;

  const response = await fetch(url);
  const data = await response.json();

  if (data.code !== 'Ok' || !data.durations) {
    console.error('Matrix API error:', data);
    const rows = sourceIndices ? sourceIndices.length : locations.length;
    const cols = destIndices ? destIndices.length : locations.length;
    return Array.from({ length: rows }, () => new Array(cols).fill(Infinity));
  }

  return data.durations;
}

/**
 * Build assignments from the EXISTING contractor allocations on jobs.
 * This shows the current human-scheduled state before any AI optimization.
 */
export function buildCurrentAssignments(jobs, contractors) {
  const contractorMap = {};
  contractors.forEach(c => { contractorMap[c.id] = c; });

  const assignments = {};
  const unassigned = [];

  // Initialize assignment buckets for all active contractors
  contractors.forEach(c => {
    assignments[c.id] = {
      contractor: c,
      jobs: [],
      totalDriveTime: 0,
      totalOnSiteTime: 0,
    };
  });

  for (const job of jobs) {
    const cid = job.contractor && job.contractor.id;
    if (cid && assignments[cid]) {
      assignments[cid].jobs.push(job);
    } else if (cid && contractorMap[cid]) {
      // Contractor exists but wasn't in active list
      assignments[cid] = {
        contractor: contractorMap[cid],
        jobs: [job],
        totalDriveTime: 0,
        totalOnSiteTime: 0,
      };
    } else {
      unassigned.push(job);
    }
  }

  // Sort each contractor's jobs by time, compute schedule
  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    a.jobs.sort((x, y) => {
      const tA = toDate(x.date);
      const tB = toDate(y.date);
      return (tA ? tA.getTime() : Infinity) - (tB ? tB.getTime() : Infinity);
    });

    let cumulativeSeconds = 0;
    a.jobs = a.jobs.map((job, i) => {
      const onSiteTime = estimateJobDuration(job);
      const meters = getJobMeters(job);

      // No drive time data for current assignments (no matrix yet)
      cumulativeSeconds += onSiteTime;

      return {
        order: i + 1,
        job: job,
        driveTimeFromPrev: 0,
        onSiteTime,
        meters,
        cumulativeDriveArrival: 0,
        cumulativeLeave: cumulativeSeconds,
      };
    });

    a.totalOnSiteTime = a.jobs.reduce((s, j) => s + j.onSiteTime, 0);
  }

  return { assignments, unassigned };
}

/**
 * AI-optimized assignment: distribute jobs across testers while avoiding time conflicts.
 * Jobs have fixed scheduled times — the optimizer decides WHICH tester gets each job.
 * Priority: (1) no time conflicts, (2) minimize drive time, (3) balance workload.
 */
export function assignJobsEqual(allJobs, contractors, coordJobIndices, locations, durationMatrix, options = {}) {
  const numContractors = contractors.length;

  const assignments = {};
  contractors.forEach((c, idx) => {
    assignments[c.id] = {
      contractor: c,
      jobs: [],
      totalDriveTime: 0,
      totalOnSiteTime: 0,
      _contractorIdx: idx,
    };
  });

  // Prepare all jobs with metadata
  const jobEntries = allJobs.map((job, idx) => {
    const hasCoords = coordJobIndices.has(idx);
    const matrixIdx = hasCoords ? numContractors + coordJobIndices.get(idx) : null;
    const startDate = toDate(job.date);
    const startMs = startDate ? startDate.getTime() : 0;
    const onSiteTime = estimateJobDuration(job);
    const endMs = startMs + (onSiteTime * 1000);
    return { job, hasCoords, matrixIdx, startMs, endMs, onSiteTime };
  });

  // Sort all jobs by scheduled time — assign earliest first
  jobEntries.sort((a, b) => a.startMs - b.startMs);

  // Helper: check if assigning a job to a tester would create a conflict
  function wouldConflict(testerJobs, newEntry) {
    if (!newEntry.startMs) return false; // no time = can't conflict
    for (const existing of testerJobs) {
      if (!existing.startMs) continue;
      // Check overlap: does the new job overlap with any existing job?
      if (newEntry.startMs < existing.endMs && newEntry.endMs > existing.startMs) {
        // Allow stacking unless either job is marked notFlexible
        if (!existing.job.notFlexible && !newEntry.job.notFlexible) {
          continue;
        }
        return true;
      }
    }
    return false;
  }

  // Helper: get the overlap amount in ms if a job conflicts
  function getOverlap(testerJobs, newEntry) {
    if (!newEntry.startMs) return 0;
    let maxOverlap = 0;
    for (const existing of testerJobs) {
      if (!existing.startMs) continue;
      const overlapStart = Math.max(newEntry.startMs, existing.startMs);
      const overlapEnd = Math.min(newEntry.endMs, existing.endMs);
      if (overlapEnd > overlapStart) {
        maxOverlap = Math.max(maxOverlap, overlapEnd - overlapStart);
      }
    }
    return maxOverlap;
  }

  // Helper: get drive time from tester's last job to new job
  function getDriveTime(testerJobs, newEntry, contractorIdx) {
    if (!newEntry.hasCoords || newEntry.matrixIdx == null) return 0;
    if (testerJobs.length === 0) {
      // Drive from contractor start
      const dur = durationMatrix[contractorIdx][newEntry.matrixIdx];
      return (dur && dur !== Infinity) ? dur : 0;
    }
    // Drive from last job with coords
    for (let i = testerJobs.length - 1; i >= 0; i--) {
      if (testerJobs[i].matrixIdx != null) {
        const dur = durationMatrix[testerJobs[i].matrixIdx][newEntry.matrixIdx];
        return (dur && dur !== Infinity) ? dur : 0;
      }
    }
    return durationMatrix[contractorIdx][newEntry.matrixIdx] || 0;
  }

  // Assign each job to the best tester
  const maxPerTester = Math.ceil(allJobs.length / numContractors) + 2; // allow slight imbalance
  const useRegionFilter = options.regionFilter !== false && options.regionFilter;
  const maxSegment = options.maxSegmentDriveSeconds || null;
  const maxDaily = options.maxDailyDriveSeconds || null;
  const mode = options.mode || 'optimal';

  for (const entry of jobEntries) {
    let bestCid = null;
    let bestScore = Infinity;
    let fallbackCid = null;
    let fallbackScore = Infinity;

    for (const c of contractors) {
      const a = assignments[c.id];
      if (a.jobs.length >= maxPerTester) continue;

      // Region filter: skip contractor if regions don't match
      if (useRegionFilter && c.region && entry.job.region && c.region !== entry.job.region) continue;

      const driveTime = getDriveTime(a.jobs, entry, a._contractorIdx);

      // Max segment drive: skip if single drive exceeds limit
      if (maxSegment && driveTime > maxSegment) continue;

      // Max daily drive: skip if total daily drive would exceed limit
      if (maxDaily && (a.totalDriveTime + driveTime) > maxDaily) continue;

      const jobCount = a.jobs.length;
      // Score: lower is better. Mode determines weighting.
      const score = mode === 'roundrobin'
        ? (jobCount * 600) + driveTime   // heavy balance weight
        : driveTime + (jobCount * 120);  // optimal: minimize drive, slight balance

      if (!wouldConflict(a.jobs, entry)) {
        // No conflict — this is a valid candidate
        if (score < bestScore) {
          bestScore = score;
          bestCid = c.id;
        }
      } else {
        // Would conflict — track as fallback
        const overlap = getOverlap(a.jobs, entry);
        const fallScore = overlap + driveTime;
        if (fallScore < fallbackScore) {
          fallbackScore = fallScore;
          fallbackCid = c.id;
        }
      }
    }

    // Assign to best non-conflicting tester, or least-conflicting as last resort
    const targetCid = bestCid || fallbackCid || contractors[0].id;
    const assignedDriveTime = getDriveTime(assignments[targetCid].jobs, entry, assignments[targetCid]._contractorIdx);
    assignments[targetCid].totalDriveTime += assignedDriveTime;
    assignments[targetCid].jobs.push(entry);
  }

  // Sort each tester's jobs by scheduled time
  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    a.jobs.sort((x, y) => x.startMs - y.startMs);
  }

  // Recompute drive times + cumulative schedule
  for (let ci = 0; ci < contractors.length; ci++) {
    const c = contractors[ci];
    const a = assignments[c.id];
    const startIdx = ci;

    a.totalDriveTime = 0;
    a.totalOnSiteTime = 0;
    let prevLocationIdx = startIdx;

    const firstJobDate = a.jobs.length > 0 ? toDate(a.jobs[0].job.date) : null;
    const dayStartMs = firstJobDate ? firstJobDate.getTime() : 0;

    a.jobs = a.jobs.map((entry, i) => {
      const jobDate = toDate(entry.job.date);
      const jobStartMs = jobDate ? jobDate.getTime() : 0;
      const onSiteTime = entry.onSiteTime;
      const meters = getJobMeters(entry.job);

      let driveTime = 0;
      if (entry.hasCoords && entry.matrixIdx != null) {
        driveTime = durationMatrix[prevLocationIdx][entry.matrixIdx] || 0;
        if (driveTime === Infinity) driveTime = 0;
        prevLocationIdx = entry.matrixIdx;
      }

      const offsetFromStart = dayStartMs > 0 && jobStartMs > 0
        ? Math.max(0, (jobStartMs - dayStartMs) / 1000)
        : 0;

      a.totalDriveTime += driveTime;
      a.totalOnSiteTime += onSiteTime;

      return {
        order: i + 1,
        job: entry.job,
        driveTimeFromPrev: driveTime,
        onSiteTime,
        meters,
        cumulativeDriveArrival: offsetFromStart,
        cumulativeLeave: offsetFromStart + onSiteTime,
      };
    });

    delete a._contractorIdx;
  }

  // Detect remaining (unavoidable) time conflicts
  const conflicts = detectTimeConflicts(assignments);

  return { assignments, conflicts };
}

/**
 * Post-assignment validation: checks each assignment for rule violations.
 * Returns { valid, violations } where violations is an array of { tester, job, rule, detail }.
 */
export function reviewAssignments(assignments, options = {}) {
  const violations = [];
  const useRegionFilter = options.regionFilter !== false && options.regionFilter;
  const maxSegment = options.maxSegmentDriveSeconds || null;
  const maxDaily = options.maxDailyDriveSeconds || null;

  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    const testerName = a.contractor.name || cid;
    const testerRegion = a.contractor.region;

    let dailyDrive = 0;

    for (const j of a.jobs) {
      const driveTime = j.driveTimeFromPrev || 0;
      dailyDrive += driveTime;

      // Region mismatch
      if (useRegionFilter && testerRegion && j.job.region && testerRegion !== j.job.region) {
        violations.push({
          tester: testerName,
          job: j.job.company || j.job.projectName || j.job.id,
          rule: 'region_mismatch',
          detail: `Tester region "${testerRegion}" does not match job region "${j.job.region}"`
        });
      }

      // Segment drive exceeded
      if (maxSegment && driveTime > maxSegment) {
        violations.push({
          tester: testerName,
          job: j.job.company || j.job.projectName || j.job.id,
          rule: 'segment_drive_exceeded',
          detail: `Drive segment ${Math.round(driveTime / 60)} min exceeds max ${Math.round(maxSegment / 60)} min`
        });
      }
    }

    // Daily drive exceeded
    if (maxDaily && dailyDrive > maxDaily) {
      violations.push({
        tester: testerName,
        job: null,
        rule: 'daily_drive_exceeded',
        detail: `Total daily drive ${Math.round(dailyDrive / 60)} min exceeds max ${Math.round(maxDaily / 60)} min`
      });
    }
  }

  return { valid: violations.length === 0, violations };
}

/**
 * Calculate efficiency score with letter grade and actionable insights
 */
export function calculateEfficiency(assignments, contractors, durationMatrix, numContractors, compareAgainst) {
  let totalDriveTime = 0;
  let totalOnSiteTime = 0;
  let totalJobs = 0;
  const insights = [];
  let longGaps = 0;

  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    totalDriveTime += a.totalDriveTime || 0;
    totalOnSiteTime += a.totalOnSiteTime || 0;
    totalJobs += a.jobs.length;

    for (const j of a.jobs) {
      if (j.driveTimeFromPrev > 2700) {
        longGaps++;
        const prevJob = a.jobs[j.order - 2];
        const prevName = prevJob ? (prevJob.job.company || prevJob.job.projectName || 'Start') : 'Start';
        const currName = j.job.company || j.job.projectName || j.job.projectId || 'Job';
        insights.push({
          type: 'long_gap',
          text: `${a.contractor.name} drives ${Math.round(j.driveTimeFromPrev / 60)} min between ${prevName} → ${currName}`,
          action: null
        });
      }
    }
  }

  // Geographic clustering
  const jobsByArea = {};
  for (const cid of Object.keys(assignments)) {
    for (const j of assignments[cid].jobs) {
      const addr = j.job.address;
      let area = null;
      if (addr && typeof addr === 'object' && addr.locality) area = addr.locality;
      else if (addr && typeof addr === 'object' && addr.administrative_area_level_2) area = addr.administrative_area_level_2;
      if (!area) continue;
      if (!jobsByArea[area]) jobsByArea[area] = [];
      jobsByArea[area].push({ contractorId: cid, contractorName: assignments[cid].contractor.name, job: j.job });
    }
  }
  for (const [area, items] of Object.entries(jobsByArea)) {
    const uniqueContractors = [...new Set(items.map(i => i.contractorId))];
    if (uniqueContractors.length > 1 && items.length >= 3) {
      const countByCid = {};
      items.forEach(i => { countByCid[i.contractorId] = (countByCid[i.contractorId] || 0) + 1; });
      const targetCid = Object.entries(countByCid).sort((a, b) => b[1] - a[1])[0][0];
      const targetName = assignments[targetCid].contractor.name;
      const jobsToMove = items.filter(i => i.contractorId !== targetCid);
      insights.push({
        type: 'consolidate',
        text: `${items.length} jobs in ${area} split across ${uniqueContractors.length} testers - consolidate to ${targetName}`,
        action: {
          type: 'consolidate',
          moves: jobsToMove.map(i => ({ jobId: i.job.id, fromCid: i.contractorId, toCid: targetCid }))
        }
      });
    }
  }

  // Swap suggestions
  if (durationMatrix && numContractors != null) {
    const swaps = findSwapSuggestions(assignments, contractors, durationMatrix, numContractors);
    swaps.forEach(s => insights.push(s));
  }

  // Job count imbalance
  const jobCounts = Object.values(assignments).filter(a => a.jobs.length > 0).map(a => a.jobs.length);
  if (jobCounts.length > 1) {
    const maxJobs = Math.max(...jobCounts);
    const minJobs = Math.min(...jobCounts);
    if (maxJobs > 0 && minJobs > 0 && maxJobs - minJobs > 3) {
      const heavy = Object.values(assignments).find(a => a.jobs.length === maxJobs);
      const light = Object.values(assignments).find(a => a.jobs.length === minJobs);
      if (heavy && light) {
        insights.push({
          type: 'imbalance',
          text: `${heavy.contractor.name} has ${maxJobs} jobs, ${light.contractor.name} has ${minJobs} - uneven workload`,
          action: null
        });
      }
    }
  }

  // Detect time conflicts
  const conflicts = detectTimeConflicts(assignments);
  const conflictCount = conflicts.length;

  // Utilization ratio
  const utilizationPercent = (totalOnSiteTime + totalDriveTime) > 0
    ? Math.round((totalOnSiteTime / (totalOnSiteTime + totalDriveTime)) * 100)
    : 0;

  // Per-tester estimated day-end times
  const testerDayEnds = {};
  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    if (a.jobs.length === 0) continue;
    const lastJob = a.jobs[a.jobs.length - 1];
    const lastDate = toDate(lastJob.job.date);
    if (lastDate) {
      const endMs = lastDate.getTime() + (lastJob.onSiteTime * 1000);
      const endDate = new Date(endMs);
      testerDayEnds[cid] = {
        name: a.contractor.name,
        endTime: endDate.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }),
        endMs
      };
    }
  }

  // Score
  const totalDriveMinutes = totalDriveTime / 60;
  const baselineDriveMinutes = totalJobs * 15;
  const excessMinutes = Math.max(0, totalDriveMinutes - baselineDriveMinutes);

  let score = 100;
  score -= Math.floor(excessMinutes / 10);
  score -= longGaps * 3;
  score -= conflictCount * 5;
  score = Math.max(0, Math.min(100, score));

  let longestGap = 0;
  for (const cid of Object.keys(assignments)) {
    for (const j of assignments[cid].jobs) {
      if (j.driveTimeFromPrev > longestGap) longestGap = j.driveTimeFromPrev;
    }
  }

  // Savings compared to another plan
  let savingsSeconds = 0;
  let savingsText = null;
  if (compareAgainst) {
    savingsSeconds = compareAgainst.totalDriveTime - totalDriveTime;
    if (savingsSeconds > 60) {
      savingsText = `Save ${formatDuration(savingsSeconds)} of drive time with this plan`;
    }
  }

  const activeTesters = Object.values(assignments).filter(a => a.jobs.length > 0).length;

  return {
    score,
    grade: getLetterGrade(score),
    totalDriveTime,
    totalDriveTimeFormatted: formatDuration(totalDriveTime),
    totalOnSiteTime,
    totalOnSiteTimeFormatted: formatDuration(totalOnSiteTime),
    avgJobsPerTester: activeTesters > 0 ? Math.round((totalJobs / activeTesters) * 10) / 10 : 0,
    longestGap,
    longestGapFormatted: formatDuration(longestGap),
    totalJobs,
    activeTesters,
    insights,
    savingsSeconds,
    savingsText,
    conflictCount,
    utilizationPercent,
    testerDayEnds,
  };
}

function getLetterGrade(score) {
  if (score >= 95) return 'A+';
  if (score >= 90) return 'A';
  if (score >= 85) return 'A-';
  if (score >= 80) return 'B+';
  if (score >= 75) return 'B';
  if (score >= 70) return 'B-';
  if (score >= 65) return 'C+';
  if (score >= 60) return 'C';
  if (score >= 55) return 'C-';
  if (score >= 50) return 'D+';
  if (score >= 45) return 'D';
  if (score >= 40) return 'D-';
  return 'F';
}

function findSwapSuggestions(assignments, contractors, durationMatrix, numContractors) {
  const suggestions = [];
  const contractorIds = Object.keys(assignments).filter(cid => assignments[cid].jobs.length > 0);

  for (let ci = 0; ci < contractorIds.length && suggestions.length < 5; ci++) {
    for (let cj = ci + 1; cj < contractorIds.length && suggestions.length < 5; cj++) {
      const cidA = contractorIds[ci];
      const cidB = contractorIds[cj];
      const aJobs = assignments[cidA].jobs;
      const bJobs = assignments[cidB].jobs;

      for (let ai = 0; ai < aJobs.length && suggestions.length < 5; ai++) {
        for (let bi = 0; bi < bJobs.length && suggestions.length < 5; bi++) {
          const jobA = aJobs[ai];
          const jobB = bJobs[bi];

          const coordsA = getJobCoords(jobA.job);
          const coordsB = getJobCoords(jobB.job);
          if (!coordsA || !coordsB) continue;

          const distAtoB = haversine(coordsA.lat, coordsA.lng, coordsB.lat, coordsB.lng);

          if (distAtoB > 15000) {
            const nameA = jobA.job.company || jobA.job.projectName || 'a job';
            const nameB = jobB.job.company || jobB.job.projectName || 'a job';
            const testerA = assignments[cidA].contractor.name;
            const testerB = assignments[cidB].contractor.name;

            const areaA = jobA.job.address && jobA.job.address.locality;
            const areaB = jobB.job.address && jobB.job.address.locality;
            if (areaA && areaB && areaA !== areaB) {
              const savingEst = Math.round(distAtoB / 1000);
              suggestions.push({
                type: 'swap',
                text: `Swap "${nameA}" (${testerA}) with "${nameB}" (${testerB}) - save ~${savingEst} km`,
                action: {
                  type: 'swap',
                  jobAId: jobA.job.id,
                  jobBId: jobB.job.id,
                  cidA,
                  cidB
                }
              });
            }
          }
        }
      }
    }
  }

  return suggestions;
}

/**
 * Detect time conflicts between consecutive jobs for each contractor.
 * Returns an array of conflict objects.
 */
export function detectTimeConflicts(assignments) {
  const conflicts = [];
  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    for (let i = 0; i < a.jobs.length - 1; i++) {
      const curr = a.jobs[i];
      const next = a.jobs[i + 1];
      const currDate = toDate(curr.job.date);
      const nextDate = toDate(next.job.date);
      if (!currDate || !nextDate) continue;

      // Neither job is notFlexible — intentional overlap, not a conflict
      if (!curr.job.notFlexible && !next.job.notFlexible) {
        continue;
      }

      // Current job finishes at start + onSiteTime, then drive to next = next.driveTimeFromPrev
      const currEndMs = currDate.getTime() + (curr.onSiteTime * 1000) + ((next.driveTimeFromPrev || 0) * 1000);
      const nextStartMs = nextDate.getTime();

      if (currEndMs > nextStartMs) {
        const overlapMin = Math.round((currEndMs - nextStartMs) / 60000);
        const currName = curr.job.company || curr.job.projectName || 'Job';
        const nextName = next.job.company || next.job.projectName || 'Job';
        conflicts.push({
          contractorId: cid,
          contractorName: a.contractor.name,
          jobAId: curr.job.id,
          jobBId: next.job.id,
          overlapMinutes: overlapMin,
          text: `${a.contractor.name}: "${currName}" and "${nextName}" overlap by ${overlapMin} min`
        });
      }
    }
  }
  return conflicts;
}

/**
 * Pure swap: swap two jobs between contractors without Vue reactivity.
 */
function executeSwapPure(assignments, jobAId, cidA, jobBId, cidB) {
  const aA = assignments[cidA];
  const aB = assignments[cidB];
  if (!aA || !aB) return;
  const aIdx = aA.jobs.findIndex(j => j.job.id === jobAId);
  const bIdx = aB.jobs.findIndex(j => j.job.id === jobBId);
  if (aIdx === -1 || bIdx === -1) return;
  const jobA = aA.jobs[aIdx];
  const jobB = aB.jobs[bIdx];
  aA.jobs.splice(aIdx, 1, jobB);
  aB.jobs.splice(bIdx, 1, jobA);
}

/**
 * Pure move: move a job from one contractor to another without Vue reactivity.
 */
function executeMovePure(assignments, jobId, fromCid, toCid) {
  const from = assignments[fromCid];
  const to = assignments[toCid];
  if (!from || !to) return;
  const idx = from.jobs.findIndex(j => j.job.id === jobId);
  if (idx === -1) return;
  const [removed] = from.jobs.splice(idx, 1);
  to.jobs.push(removed);
}

/**
 * Recalculate order, on-site times, totals after mutations — pure, no Vue $set.
 */
function recalculatePure(assignments) {
  const sortByTime = (a, b) => {
    const tA = toDate(a.job.date);
    const tB = toDate(b.job.date);
    return (tA ? tA.getTime() : Infinity) - (tB ? tB.getTime() : Infinity);
  };
  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    a.jobs.sort(sortByTime);
    a.jobs.forEach((j, i) => {
      j.order = i + 1;
      j.onSiteTime = estimateJobDuration(j.job);
      j.meters = getJobMeters(j.job);
    });
    a.totalDriveTime = a.jobs.reduce((sum, j) => sum + (j.driveTimeFromPrev || 0), 0);
    a.totalOnSiteTime = a.jobs.reduce((sum, j) => sum + (j.onSiteTime || 0), 0);
  }
}

/**
 * Deep-clone assignments so mutations don't affect the original.
 */
function cloneAssignments(assignments) {
  const clone = {};
  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    clone[cid] = {
      contractor: a.contractor,
      jobs: a.jobs.map(j => ({ ...j, job: j.job })),
      totalDriveTime: a.totalDriveTime,
      totalOnSiteTime: a.totalOnSiteTime,
    };
  }
  return clone;
}

/**
 * Self-optimizing loop: applies insight actions iteratively until A+ or no actionable insights remain.
 * Yields to UI thread between passes for progress display.
 * Returns { assignments, efficiency, iterations, gradeHistory }
 */
export async function selfOptimize(assignments, contractors, matrix, numContractors, compareAgainst, maxIter = 10, onProgress) {
  let current = cloneAssignments(assignments);
  const gradeHistory = [];
  let iterations = 0;
  let eff;

  for (let pass = 0; pass < maxIter; pass++) {
    iterations = pass + 1;

    eff = calculateEfficiency(current, contractors, matrix, numContractors, compareAgainst);
    gradeHistory.push(eff.grade);

    if (onProgress) onProgress({ pass: iterations, maxIter, grade: eff.grade, score: eff.score });

    // Stop if already A+ or no actionable insights
    const actionable = eff.insights.filter(i => i.action);
    if (eff.score >= 95 || actionable.length === 0) break;

    // Apply all actionable insights
    for (const insight of actionable) {
      const action = insight.action;
      if (action.type === 'swap') {
        executeSwapPure(current, action.jobAId, action.cidA, action.jobBId, action.cidB);
      } else if (action.type === 'consolidate') {
        for (const move of action.moves) {
          executeMovePure(current, move.jobId, move.fromCid, move.toCid);
        }
      }
    }

    recalculatePure(current);

    // Yield to UI thread
    await new Promise(r => setTimeout(r, 0));
  }

  // Final efficiency calc
  eff = calculateEfficiency(current, contractors, matrix, numContractors, compareAgainst);
  if (gradeHistory[gradeHistory.length - 1] !== eff.grade) {
    gradeHistory.push(eff.grade);
  }

  return { assignments: current, efficiency: eff, iterations, gradeHistory };
}

/**
 * Calculate start/finish times for each contractor based on their assigned jobs.
 * Start = first job time − drive time to first job − 15 min buffer
 * Finish = last job time + last job duration + 30 min buffer
 */
export function calculateTesterTimes(assignments) {
  const BUFFER_START_MS = 15 * 60 * 1000; // 15 min
  const BUFFER_FINISH_MS = 30 * 60 * 1000; // 30 min
  const result = {};

  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    if (!a.jobs || a.jobs.length === 0) continue;

    const firstJob = a.jobs[0];
    const lastJob = a.jobs[a.jobs.length - 1];

    const firstDate = toDate(firstJob.job.date);
    const lastDate = toDate(lastJob.job.date);
    if (!firstDate || !lastDate) continue;

    const driveToFirstMs = (firstJob.driveTimeFromPrev || 0) * 1000;
    const startMs = firstDate.getTime() - driveToFirstMs - BUFFER_START_MS;
    const finishMs = lastDate.getTime() + (lastJob.onSiteTime * 1000) + BUFFER_FINISH_MS;

    const startDate = new Date(startMs);
    const finishDate = new Date(finishMs);

    const startTime = startDate.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
    const finishTime = finishDate.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });

    const totalHours = Math.round((finishMs - startMs) / 3600000 * 10) / 10;

    const totalOnSiteSeconds = a.jobs.reduce((sum, j) => sum + (j.onSiteTime || 0), 0);
    const totalDriveSeconds = a.jobs.reduce((sum, j) => sum + (j.driveTimeFromPrev || 0), 0);

    result[cid] = {
      name: a.contractor.name,
      startTime,
      finishTime,
      startMs,
      finishMs,
      totalHours,
      jobCount: a.jobs.length,
      totalOnSiteSeconds,
      totalDriveSeconds,
    };
  }

  return result;
}

export function clearCache() {
  matrixCache = {};
}

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Find pairs of consecutive flexible jobs that overlap in time (stacked).
 * Returns array of { jobAId, jobBId, contractorId }.
 */
export function findStackedPairs(assignments) {
  const pairs = [];
  for (const cid of Object.keys(assignments)) {
    const a = assignments[cid];
    for (let i = 0; i < a.jobs.length - 1; i++) {
      const curr = a.jobs[i];
      const next = a.jobs[i + 1];
      if (curr.job.notFlexible || next.job.notFlexible) continue;
      const currDate = toDate(curr.job.date);
      const nextDate = toDate(next.job.date);
      if (!currDate || !nextDate) continue;
      const currEndMs = currDate.getTime() + (curr.onSiteTime * 1000);
      const nextStartMs = nextDate.getTime();
      // They overlap in time = stacked pair
      if (nextStartMs < currEndMs) {
        pairs.push({ jobAId: curr.job.id, jobBId: next.job.id, contractorId: cid });
      }
    }
  }
  return pairs;
}

export function formatDuration(seconds) {
  if (!seconds || seconds === Infinity) return '0 min';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remMin = mins % 60;
  return `${hrs}h ${remMin}m`;
}

export const CONTRACTOR_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1',
  '#84cc16', '#e11d48', '#0ea5e9', '#d946ef', '#facc15',
  '#a3e635', '#f43f5e', '#38bdf8', '#c084fc', '#fbbf24',
];

export function getContractorColor(index) {
  return CONTRACTOR_COLORS[index % CONTRACTOR_COLORS.length];
}
